/**
 * IntelligenceNoteView — officer-facing layout for AI Intelligence Notes.
 * Renders parsed markdown as titled sections with scannable bullet points
 * (no raw # / ## / ** markup).
 */
import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { parseIntelligenceNote } from '../../utils/parseIntelligenceNote';

interface IntelligenceNoteViewProps {
    markdown: string;
    /** Optional eyebrow above the title (e.g. "Graph summary") */
    eyebrow?: string;
    className?: string;
    /** Cap height and scroll when content is long */
    maxHeightClass?: string;
}

/** Inline bold segments from leftover ** markers (defensive). */
function renderInline(text: string): React.ReactNode {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    if (parts.length === 1) return text;
    return parts.map((part, i) => {
        const m = part.match(/^\*\*(.+)\*\*$/);
        if (m) {
            return (
                <strong key={i} className="font-semibold text-foreground">
                    {m[1]}
                </strong>
            );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}

export const IntelligenceNoteView: React.FC<IntelligenceNoteViewProps> = ({
    markdown,
    eyebrow,
    className,
    maxHeightClass = 'max-h-72',
}) => {
    const parsed = useMemo(() => parseIntelligenceNote(markdown), [markdown]);

    if (!markdown?.trim()) return null;

    if (parsed.sections.length === 0) {
        return (
            <div className={clsx('text-xs text-foreground leading-relaxed', className)}>
                {eyebrow && (
                    <p className="text-[10px] uppercase tracking-wide font-medium text-primary mb-1.5">
                        {eyebrow}
                    </p>
                )}
                <p className="whitespace-pre-wrap break-words text-muted-foreground">{markdown}</p>
            </div>
        );
    }

    return (
        <div
            className={clsx('text-xs text-foreground', className)}
            data-testid="intelligence-note-view"
        >
            {eyebrow && (
                <p className="text-[10px] uppercase tracking-wide font-medium text-primary mb-1.5">
                    {eyebrow}
                </p>
            )}

            <div className="flex items-baseline justify-between gap-2 mb-2.5">
                <h3 className="text-sm font-semibold text-foreground tracking-tight">
                    {parsed.title}
                </h3>
            </div>

            <div
                className={clsx(
                    'space-y-3 overflow-y-auto styled-scrollbar pr-0.5',
                    maxHeightClass,
                )}
            >
                {parsed.sections.map((section, idx) => (
                    <section
                        key={`${section.heading}-${idx}`}
                        className="rounded-md border border-border/60 bg-background/60 px-2.5 py-2"
                    >
                        <h4 className="text-[11px] font-semibold text-foreground/90 mb-1.5 tracking-wide">
                            {section.heading}
                        </h4>
                        <ul className="space-y-1.5">
                            {section.bullets.map((bullet, bi) => (
                                <li
                                    key={bi}
                                    className="flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground"
                                >
                                    <span
                                        className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary/70"
                                        aria-hidden
                                    />
                                    <span className="min-w-0 break-words text-foreground/85">
                                        {renderInline(bullet)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </div>
        </div>
    );
};
